import styled from '@emotion/styled';
import _ from 'lodash';
import { useState } from 'react';

import { ContractorBadges } from './ContractorBadges';
import { profileURL } from './profileURL';
import useRAQQuestionModal from '../../hooks/use_raq_question_modal';
import Modal from '../Modal';
import InfoIcon from '../OC/oc-info-icon';
import { If, RequestAQuoteModal, WrappedEmailRAQQuestionModalButton } from '../Utilities';
import { connectLocation } from '../location/LocationConnectors';

const StarRatingsLink = styled.a`
  text-decoration: inherit;
  color: #333;
  outline-color: #d40f7d;

  .star {
    color: #fbaa19;
  }

  .single-star {
    color: #fbaa19;
    font-size: 19px;
    display: inline-flex;
    margin-right: 4px;
  }

  .review-count {
    text-decoration: underline;
  }

  .rating-number {
    font-weight: 700;
    color: #000;
  }

  &:hover {
    cursor: pointer;
  }
`;

const Logo = styled.picture`
  display: none;

  @media (min-width: 768px) {
    display: inline-block;
    margin-right: 15px;
  }

  img {
    width: 50px;
  }
`;

const AdditionalLanguageBadge = styled.span`
  background-color: #eeeaea;
  margin-left: 0.4rem;
  padding: 0.2rem;
`;

export const ContractorReviews = connectLocation(({ t, count, masDealerCode, storeName, locale, stars }) => {
  const [starRatingModalOpen, starRatingModalOpenChangeState] = useState(false);

  const displayStars = stars || 0;
  return (
    <div className="reviews">
      <StarRatingsLink
        data-track="view-profile"
        data-track-contractor={storeName}
        data-track-contractor-id={masDealerCode}
        href={profileURL(masDealerCode, locale?.code)}
      >
        <div className="rating-display">
          <span className="single-star">★</span>
          <span className="rating-number">{displayStars}</span>
        </div>
        <div className="review-count">
          {count} {t('contractors.block.reviews')}
        </div>
      </StarRatingsLink>
      <InfoIcon clickHandler={() => starRatingModalOpenChangeState(true)} marginLeft="4px" />
      <Modal
        className="star-rating-modal"
        open={starRatingModalOpen}
        onClose={() => starRatingModalOpenChangeState(false)}
      >
        <h2 className="h3">{t('contractors.star_ratings_title')}</h2>
        <p>{t('contractors.star_ratings_p1')}</p>
        <p>{t('contractors.star_ratings_p2')}</p>
        <a
          href="https://pages.guildquality.com/partner/owenscorning/"
          className="cta-button"
          data-track="learn-more"
          data-track-element-location="main section CTA"
          data-track-destination="Star Ratings"
        >
          {t('contractors.learn_more_star_ratings')}
        </a>
      </Modal>
    </div>
  );
});

ContractorReviews.displayName = 'ContractorReviews';

export const ContractorBlock = connectLocation(({ t, locale, ...props }) => {
  const { RAQOpen, RAQOnOpen, RAQOnClose, RAQFormName, RAQModalElementFunc } = useRAQQuestionModal();
  const phoneDigits = _.toString(props.phone).replace(/[^0-9A-Z]/g, '');
  const formattedPhone = phoneDigits.replace(/(.{3})(.{3})(.{4,})/, '($1) $2-$3');
  const hasSpanishLanguageBadge = props?.active_certifications?.includes('SS') || false;

  return (
    <div className="contractor">
      <RequestAQuoteModal
        contractor={props}
        open={RAQOpen}
        portal={RAQModalElementFunc}
        raqFormName={RAQFormName}
        onClose={RAQOnClose}
      />
      <Logo>
        <source
          media="(min-width: 768px)"
          srcSet={
            props.logo ||
            'https://imagecdn.owenscorning.com/ocimages/image/upload/f_auto,w_65/v1589224258/roofing/contractors/house-generic.png'
          }
        />
        <img src="" alt="" />
      </Logo>

      <div className="name-location">
        <a
          href={profileURL(props.mas_dealer_code, locale?.code)}
          className="name"
          tabIndex={0}
          data-track="learn-more"
          data-track-element-location="main section text link"
          data-track-destination="Contractor Profile"
          data-track-professional="contractor"
        >
          <h3 className="body-font">{props.store_name}</h3>
        </a>

        <p>
          {props.city}, {props.state}{' '}
          {hasSpanishLanguageBadge && (
            <AdditionalLanguageBadge>{t('contractors.additional_language_badge')}</AdditionalLanguageBadge>
          )}{' '}
        </p>
        <div className="mobile-switch">
          <div className="contact-info">
            <p>
              <a
                href={`tel:${phoneDigits}`}
                className="phone"
                tabIndex={0}
                data-track="phone"
                data-track-professional="contractor"
                data-track-contractor-id={props.mas_dealer_code}
                data-track-contractor={props.store_name}
              >
                {formattedPhone}
              </a>
              <If condition={props.has_email}>
                <span>|</span>
                <WrappedEmailRAQQuestionModalButton contractor={props}>
                  {({ onClick }) => (
                    <button
                      type="button"
                      onClick={onClick}
                      className="link-text"
                      data-track="open-modal"
                      data-track-modal-name="roof-replacement-modal"
                      data-track-contractor={props.store_name}
                      data-track-contractor-id={props.mas_dealer_code}
                      data-track-form-name="email-contractor"
                    >
                      {t('contractors.email')}
                    </button>
                  )}
                </WrappedEmailRAQQuestionModalButton>
              </If>
              <If condition={props.website}>
                <span>|</span>
                <a
                  href={_.get(props, 'website')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-text"
                  tabIndex={0}
                  data-track="website"
                  data-track-contractor={props.store_name}
                  data-track-contractor-id={props.mas_dealer_code}
                  data-track-professional="contractor"
                >
                  {t('contractors.website')}
                </a>
              </If>
            </p>
          </div>

          <If condition={props.active_certifications && props.active_certifications.length > 0}>
            <ul className="professional-certs">
              <ContractorBadges active_certifications={props.active_certifications} locale={locale} t={t} />
            </ul>
          </If>
        </div>

        <If condition={props.show_request_a_quote}>
          <button
            type="button"
            className="oc-cta-button mobile-only"
            onClick={RAQOnOpen}
            data-track="form-start"
            data-track-form-type="lead"
            data-track-form-name="request-a-quote"
            data-track-contractor={props.store_name}
            data-track-contractor-id={props.mas_dealer_code}
            data-track-professional="contractor"
          >
            {t('contractors.request_quote')}
          </button>
        </If>
      </div>

      <div className="reviews-quote">
        <If condition={props.is_rating_available}>
          <ContractorReviews
            count={props.gq_review_count}
            masDealerCode={props.mas_dealer_code}
            stars={props.gq_review_stars}
            storeName={props.store_name}
          />
        </If>

        <If condition={props.show_request_a_quote}>
          <button
            type="button"
            className="oc-cta-button tablet-desktop-only"
            data-track="form-start"
            data-track-form-type="lead"
            data-track-form-name="request-a-quote"
            data-track-contractor={props.store_name}
            data-track-contractor-id={props.mas_dealer_code}
            data-track-professional="contractor"
            onClick={RAQOnOpen}
          >
            {t('contractors.request_quote')}
          </button>
        </If>
      </div>
    </div>
  );
});

ContractorBlock.propTypes = {};

ContractorBlock.WrappedComponent.displayName = 'ContractorBlock';
